import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
    Alert,
    Button,
    CircularProgress,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import api from "../../../conf/api.js";
import Cookies from "js-cookie";
import InfoIcon from "@mui/icons-material/Info";
import axios from "axios";
import UserSelector from "../../../Components/Selectors/UserSelector.js";
import { connect } from "react-redux";
import { createSHA256 } from 'hash-wasm';

const useBeforeUnload = ({ when, message }) => {
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = message;
            return message;
        };

        if (when) {
            window.addEventListener("beforeunload", handleBeforeUnload);
        }

        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [when, message]);
};
const videoFormats = [
    "mp4",
    "webm",
    "mkv",
    "mov",
    "flv",
    "wmv",
    "mpeg",
    "mpg",
    "ogv",
];

const VideoUploader = (props) => {
    const [inProcess, setProcess] = useState(false);
    const [videos, setVideos] = useState([]);
    const [errors, setErrors] = useState([]);
    const [uploading, setUploading] = useState({ uploaded: false });
    const [hash_checking, setHash] = useState([]);
    const [user, setUser] = useState(null);

    const onDrop = async (acceptedFiles) => {
        for (const file of acceptedFiles) {
            const formate = file.path.split(".").splice(-1)[0];
            let processedFileName = file.name;
            if (file.name.length > 50) {
                processedFileName = file.name.slice(0, 50);
                setErrors([
                    {
                        show: true,
                        severity: "info",
                        message: `Video title "${file.name.slice(0, 20)}..." was automatically shortened to 50 characters.`,
                    },
                ]);
            }
            if (!videoFormats.includes(formate.toLowerCase())) {
                setErrors([
                    {
                        show: true,
                        severity: "warning",
                        message: `${file.name} not spported.`,
                    },
                ]);
            } else if (file.type.startsWith("video/")) {
                // Load the video to get its duration
                const video = document.createElement("video");
                video.src = URL.createObjectURL(file);
                video.onload = () => {
                    // console.log(video);
                };
                video.onloadedmetadata = async () => {
                    const duration = video.duration;
                    // if (duration >= 20) {
                    const newVideo = {
                        loading: false,
                        title: processedFileName.replace(/\.[^/.]+$/, ""),
                        file: file,
                        size: file.size,
                        extension: file.name.split(".").pop(),
                        duration: duration,
                    };
                    // if duration and title match the we consider it exist already
                    if (
                        videos.find(
                            (f) =>
                                f.title === processedFileName.replace(/\.[^/.]+$/, "") && duration === f.duration
                        )
                    ) {
                        setErrors([
                            {
                                show: true,
                                severity: "warning",
                                message: `Video ${file.name} already exist with same duration ${duration}.`,
                            },
                        ]);
                        return;
                    }
                    setVideos((prevVideos) => [...prevVideos, newVideo]);
                    // } else {
                    //   // Notify the user that the video is too short
                    //   setErrors([
                    //     {
                    //       show: true,
                    //       severity: "warning",
                    //       message: `Video ${file.name} is too short. It should be at least 20 seconds.`,
                    //     },
                    //   ]);
                    // }
                    // Clean up the video element
                    URL.revokeObjectURL(video.src);
                };
            } else {
                setErrors([
                    {
                        show: true,
                        severity: "warning",
                        message: `${file.name}-(${file.type}) not allowed!`,
                    },
                ]);
            }
        }
    };
    const getChunkSize = (fileSize) => {
        if (fileSize < 50 * 1024 * 1024) return 10 * 1024 * 1024;       // <50MB → 15MB
        if (fileSize < 200 * 1024 * 1024) return 15 * 1024 * 1024;      // <200MB → 50MB
        if (fileSize < 1 * 1024 * 1024 * 1024) return 30 * 1024 * 1024; // <1GB → 80MB
        if (fileSize < 4 * 1024 * 1024 * 1024) return 40 * 1024 * 1024; // <4GB → 25MB
        return 60 * 1024 * 1024;
    };
    async function calculateSHA256Stream(file) {
        const chunkSize = getChunkSize(file.size);
        const totalChunks = Math.ceil(file.size / chunkSize);
        const sha256 = await createSHA256();

        for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize;
            const end = Math.min(file.size, start + chunkSize);
            const chunk = file.slice(start, end);

            const buffer = await chunk.arrayBuffer(); // Small chunk only
            sha256.update(new Uint8Array(buffer));

            // Optional: show hash progress indicator
            setHash(prev => [
                ...prev.filter(h => h.id !== file.name),
                { id: file.name, loading: true, percent: Math.round((i / totalChunks) * 100) },
            ]);
        }

        const hash = sha256.digest(); // hex string
        setHash(prev => prev.filter(h => h.id !== file.name));
        return hash;
    }
    const checkFileInDB = async (file) => {
        const hash = await calculateSHA256Stream(file);
        console.log("File Hash", hash)
        const res = await api.post("/videos/check-duplicate/", { file_hash: hash });

        const data = await res.data;
        return { isDublicated: data.duplicate, message: data.message };
    }
    const removeVideo = (index, event) => {
        event.stopPropagation();
        const updatedVideos = [...videos];
        const removedVideo = updatedVideos[index];

        // Stop the ongoing progress simulation (if any)
        if (removedVideo.loading) {
            // Clear the progress simulation timeout
            removedVideo.loading = false;
            clearTimeout(removedVideo.progressTimeout);
        }

        updatedVideos.splice(index, 1);
        setVideos(updatedVideos);
    };
    const handleVideoTitle = (index) => (e) => {
        if (e.target.value.length <= 50) {
            // let _videos = [...videos];
            // _videos[index] = { ..._videos[index], title: e.target.value };
            // setVideos(_videos);
            setVideos(prevVideos => {
                const updated = [...prevVideos];
                updated[index] = { ...updated[index], title: e.target.value };
                return updated;
            });
        } else {
            document.getElementById(e.target.id).value = e.target.value.slice(0, 15);
        }
    };

    async function uploadChunkWithRetry(formData, upload_id, idx, chunkSize, fileSize, onProgress) {
        const MAX_RETRIES = 3;
        const TIMEOUT = 1000 * 60 * 5;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
                await api.post(`/videos/upload/chunk/`,
                    formData,
                    {
                        baseURL: process.env.REACT_APP_UPLOAD_URL,
                        timeout: TIMEOUT,
                        onUploadProgress: ({ loaded }) => {
                            const bytesSent = idx * chunkSize + loaded;
                            const percent = Math.min((bytesSent / fileSize) * 100, 100);
                            onProgress(percent);
                        },
                    }
                );
                return; // success, exit function
            } catch (error) {
                console.warn(`Chunk ${idx} upload failed on attempt ${attempt}`, error);
                if (attempt === MAX_RETRIES) {
                    throw new Error(`Failed to upload chunk ${idx} after ${MAX_RETRIES} attempts`);
                }
                // Optional: wait a bit before retry
                await new Promise(res => setTimeout(res, 1000 * attempt)); // backoff: 1s, 2s, 3s
            }
        }
    }
    const uploadSingleVideo = async (video, onProgress) => {
        const { file, title, duration } = video;
        const fileExtension = file.name.split('.').pop();
        const chunkSize = getChunkSize(file.size);
        // 1) INIT
        const initResp = await api.post(`/videos/upload/init/`, { filename: title, total_size: file.size, duration: duration, fileextension: `.${fileExtension}` });
        const upload_id = initResp.data.upload_id;

        // 2) CHUNKS
        const totalChunks = Math.ceil(file.size / chunkSize);
        for (let idx = 0; idx < totalChunks; idx++) {
            const start = idx * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const chunk = file.slice(start, end);

            const formData = new FormData();
            formData.append("upload_id", upload_id);
            formData.append("chunk_index", idx);
            formData.append("chunk", chunk);

            await uploadChunkWithRetry(formData, upload_id, idx, chunkSize, file.size, onProgress);
        }

        // 3) COMPLETE
        await api.post(`/videos/upload/complete/`, { upload_id, user: user?.username || undefined }, { timeout: 1000 * 60 * 10 });

        // done!
        onProgress(100);
    };

    const uploadVideos = async () => {
        setUploading({ uploaded: true });
        setProcess(true);
        setErrors([]);
        let updatedVideos = [];
        for (const v of videos) {
            await checkFileInDB(v.file).then(({ isDublicated, message }) => {

                if (isDublicated) {
                    setErrors(prev => [
                        ...prev,
                        { show: true, severity: "warning", message: `${v.title} ${message}.` },
                    ]);
                } else {
                    updatedVideos.push({
                        ...v,
                        file: v.file,
                        loading: true,
                    });
                }
            })
        }
        setVideos(updatedVideos);

        for (let i = 0; i < updatedVideos.length; i++) {
            try {
                await uploadSingleVideo(updatedVideos[i], percent => {
                    setVideos(prev => {
                        const copy = [...prev];
                        copy[i] = {
                            ...copy[i],
                            file: {
                                ...copy[i].file,
                                uploadedPercentage: percent,
                            },
                        };
                        return copy;
                    });
                });
                // success message
                setErrors(prev => [
                    ...prev,
                    { show: true, severity: "success", message: `${updatedVideos[i].title} uploaded successfully` },
                ]);
            } catch (error) {
                console.error("Upload error:", error);
                // handle error...
                setErrors(prev => [
                    ...prev,
                    { show: true, severity: "error", message: `Failed to upload "${updatedVideos[i].title}"` },
                ]);
                break;
            } finally {
                // mark done or stop spinner
                setVideos(prev => {
                    const copy = [...prev];
                    copy[i].loading = false;
                    return copy;
                });
            }
        }

        setProcess(false);
    };
    const GetFileSize = (file) => {
        const fileSize = file.size;
        return fileSize >= 1024 * 1024 * 1024
            ? (fileSize / (1024 * 1024 * 1024)).toFixed(2) + " GB"
            : fileSize >= 1024 * 1024
                ? (fileSize / (1024 * 1024)).toFixed(1) + " MB"
                : (fileSize / 1024).toFixed(2) + " KB"
    }
    const handleCloseAlert = (error) => (event) => {
        event.stopPropagation();
        setErrors((prev) => prev.filter((e) => e.message !== error.message));
    };
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: "video/*",
        multiple: true,
        disabled: uploading.uploaded || inProcess
    });

    useBeforeUnload({
        when: inProcess,
        message: "Are you sure you want to cancel it.",
    });

    const hasError = errors.some((e) => e.severity === "error");
    const finished = uploading.uploaded && !inProcess;
    const totalSize = videos.reduce((s, v) => s + (v.size || 0), 0);
    const resetAll = () => {
        setVideos([]);
        setErrors([]);
        setUploading({ uploaded: false });
    };
    const retry = () => {
        setErrors([]);
        setUploading({ uploaded: false });
    };
    const focusRing = {
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto", width: "100%" }}>
            {/* Header */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 600, fontSize: { xs: "1.5rem", md: "2.125rem" } }}
                    >
                        Upload Videos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Add one or more video files, review their titles, then upload.
                    </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    {finished && (
                        <Button variant="outlined" onClick={resetAll} sx={{ minHeight: 44 }}>
                            Upload more
                        </Button>
                    )}
                    {finished && hasError && (
                        <Button variant="outlined" color="warning" onClick={retry} sx={{ minHeight: 44 }}>
                            Try again
                        </Button>
                    )}
                    <Button
                        startIcon={inProcess ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                        disabled={videos.length < 1 || inProcess || uploading.uploaded}
                        onClick={uploadVideos}
                        variant="contained"
                        color="primary"
                        sx={{ minHeight: 44 }}
                    >
                        {uploading.uploaded
                            ? !inProcess
                                ? "Upload finished"
                                : "Uploading, please wait..."
                            : `Upload${videos.length ? ` ${videos.length} video${videos.length > 1 ? "s" : ""}` : ""}`}
                    </Button>
                </Stack>
            </Stack>

            <Stack spacing={3}>
                {props.user?.is_staff && (
                    <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Assigned to
                        </Typography>
                        <Box sx={{ maxWidth: 400 }}>
                            <UserSelector
                                disabled={inProcess}
                                filters={{ is_creator: true, is_promouser: false }}
                                value={user}
                                onChange={(e, _u) => {
                                    setUser(_u);
                                }}
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            Videos will be uploaded on behalf of the selected creator.
                        </Typography>
                    </Paper>
                )}

                {/* Messages */}
                {errors.length > 0 && (
                    <Stack spacing={1} aria-live="polite">
                        {errors.map((e, i) => (
                            <Alert key={`${i}-${e.message}`} onClose={handleCloseAlert(e)} severity={e.severity}>
                                {e.message}
                            </Alert>
                        ))}
                    </Stack>
                )}

                {/* Drop zone */}
                <Box
                    {...getRootProps()}
                    aria-label="Drop video files here or press Enter to browse"
                    sx={{
                        border: "2px dashed",
                        borderColor: isDragActive ? "primary.main" : "divider",
                        bgcolor: isDragActive ? "action.hover" : "background.paper",
                        borderRadius: 2,
                        p: { xs: 3, md: 5 },
                        textAlign: "center",
                        cursor: uploading.uploaded || inProcess ? "not-allowed" : "pointer",
                        opacity: uploading.uploaded || inProcess ? 0.6 : 1,
                        transition: "border-color 200ms, background-color 200ms",
                        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                        ...focusRing,
                    }}
                >
                    <input {...getInputProps()} />
                    <CloudUploadIcon
                        aria-hidden="true"
                        sx={{ fontSize: { xs: 48, md: 64 }, color: isDragActive ? "primary.main" : "text.secondary" }}
                    />
                    <Typography variant="h6" component="p" sx={{ fontWeight: 600, mt: 1 }}>
                        {isDragActive ? "Drop to add videos" : "Drag video files here"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        or choose files from your device
                    </Typography>
                    <Button
                        variant="outlined"
                        tabIndex={-1}
                        disabled={uploading.uploaded || inProcess}
                        sx={{ minHeight: 44 }}
                    >
                        Browse files
                    </Button>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mt: 2 }}
                    >
                        Supported: {videoFormats.join(", ")}
                        <Tooltip title="Only video files in these formats are accepted. Titles are limited to 50 characters.">
                            <InfoIcon fontSize="inherit" aria-label="Upload requirements" />
                        </Tooltip>
                    </Typography>
                </Box>

                {/* Hash checks */}
                {hash_checking.map(
                    (h) =>
                        h.loading && (
                            <Alert
                                key={h.id}
                                icon={<CircularProgress color="warning" size={22} />}
                                severity="warning"
                                sx={{ wordBreak: "break-word" }}
                            >
                                {`Checking "${h.id}" for duplicates (${h.percent || 0}%)...`}
                            </Alert>
                        )
                )}

                {/* File list */}
                <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, md: 3 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                            Selected videos{videos.length > 0 ? ` (${videos.length})` : ""}
                        </Typography>
                        {videos.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                Total {GetFileSize({ size: totalSize })}
                            </Typography>
                        )}
                    </Stack>

                    {inProcess && <LinearProgress color="success" sx={{ mb: 2, borderRadius: 1 }} />}

                    {videos.length === 0 ? (
                        <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: "center" }}>
                            <VideoFileIcon aria-hidden="true" sx={{ fontSize: 40, color: "text.disabled" }} />
                            <Typography variant="body2" color="text.secondary">
                                No videos selected yet. Add files above to get started.
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
                            {videos.map((file, index) => {
                                const pct = Math.round(file?.file?.uploadedPercentage || 0);
                                const isDone = !file.loading && pct >= 100;
                                const checking = hash_checking.find((h) => h.id === file.file.name)?.loading;
                                const failed = finished && hasError && !isDone;
                                return (
                                    <Box
                                        component="li"
                                        key={index}
                                        sx={{
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            flexWrap: { xs: "wrap", sm: "nowrap" },
                                        }}
                                    >
                                        <Box sx={{ display: "flex", flexShrink: 0 }} aria-hidden="true">
                                            {isDone ? (
                                                <CheckCircleIcon color="success" />
                                            ) : failed ? (
                                                <ErrorIcon color="error" />
                                            ) : file.loading ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                <VideoFileIcon color="action" />
                                            )}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, width: { xs: "calc(100% - 100px)", sm: "auto" } }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Video title"
                                                disabled={file.loading || isDone}
                                                value={file.title}
                                                id={`videofield-${index}`}
                                                onChange={handleVideoTitle(index)}
                                                helperText={`${file.title.length}/50 characters`}
                                            />
                                            {(file.loading || isDone) && (
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={pct}
                                                    color={isDone ? "success" : "primary"}
                                                    aria-label={`Upload progress for ${file.title}`}
                                                    sx={{ mt: 1, borderRadius: 1 }}
                                                />
                                            )}
                                        </Box>
                                        <Stack alignItems="flex-end" sx={{ flexShrink: 0, minWidth: 64 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {file.loading || isDone
                                                    ? `${pct}%`
                                                    : checking
                                                        ? "Checking"
                                                        : failed
                                                            ? "Failed"
                                                            : "Ready"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {GetFileSize(file)} · .{file.extension}
                                            </Typography>
                                        </Stack>
                                        <Tooltip title="Remove video">
                                            <span>
                                                <IconButton
                                                    aria-label={`Remove ${file.title}`}
                                                    disabled={file.loading || checking}
                                                    onClick={(event) => removeVideo(index, event)}
                                                    sx={{ width: 44, height: 44, ...focusRing }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Paper>
            </Stack>
        </Box>
    );
};

const mapStatesToProps = (state) => {
    return {
        user: state.user,
    };
};

export default connect(mapStatesToProps, null)(VideoUploader);
